import { searchLayoutByName } from 'redux/saga/entities/bound';
import { selectMediaState } from 'redux/modules/device';
import { selectUserLocation } from 'redux/modules/locale';
import { MSA_SEARCH_HASHES } from 'constants/msa-search-hashes';
import { deepExtract } from 'helpers/deep';
import { SEARCH_PATH } from './Search.route.path';
import { DEFAULT_KILOMETERS } from './Map/SearchMap.constants';
import { selectActiveLocation } from './SearchBox/SearchBox.reducer';
import { selectCurrentPage, selectMapView, selectIsMapToggleable } from './Search.reducer';
import { DEFAULT_GEOLOCATION, RESULTS_LAYOUT } from './Search.constants';

const selectFieldInDataOfLayout = (field, defaultValue, layout) =>
  deepExtract(`data.${field}`, layout && layout[Object.keys(layout)[0]], defaultValue);

export const selectTotalHitsInLayout = selectFieldInDataOfLayout.bind(null, 'total_hits', null);
export const selectSchedulesInLayout = selectFieldInDataOfLayout.bind(null, 'schedules', null);
export const selectVenuesInLayout = selectFieldInDataOfLayout.bind(null, 'venue_tab_items', null);
export const selectRadiusUsedInLayout = selectFieldInDataOfLayout.bind(null, 'radius_used', DEFAULT_KILOMETERS);
export const selectMapItemsInLayout = selectFieldInDataOfLayout.bind(null, 'map_items', []);
export const selectCursorInLayout = selectFieldInDataOfLayout.bind(null, 'cursor', '');
export const selectSearchIdInLayout = selectFieldInDataOfLayout.bind(null, 'search_id', null);
export const selectMetadataInLayout = selectFieldInDataOfLayout.bind(null, 'filter_metadata', {});
export const selectFiltersForHashInLayout = selectFieldInDataOfLayout.bind(null, 'filters_for_hash', {});
export const selectResolvedFilterDataInLayout = selectFieldInDataOfLayout.bind(null, 'resolved_filter_data', {});
export const selectErrorInLayout = selectFieldInDataOfLayout.bind(null, 'module_error', null);
export const selectRelatedFiltersInLayout = selectFieldInDataOfLayout.bind(null, 'related_filters', {});
export const selectKeywordAutocompleteEntryInLayout = layout =>
  deepExtract('keyword_autocomplete_entry', selectResolvedFilterDataInLayout(layout), {});
export const selectKeywordAutocompleteValueInLayout = layout =>
  selectKeywordAutocompleteEntryInLayout(layout).name || '';
export const selectLocationAutocompleteEntryInLayout = layout =>
  deepExtract('location_autocomplete_entry', selectResolvedFilterDataInLayout(layout), {});
export const selectLocationAutocompleteValueInLayout = layout =>
  selectLocationAutocompleteEntryInLayout(layout).name || '';
export const selectLocationAutocompleteTimezoneInLayout = layout =>
  selectLocationAutocompleteEntryInLayout(layout).timezone || '';
export const selectCalculatedRadiusKmInLayout = layout =>
  deepExtract('location_autocomplete_entry.calculated_radius_km', selectResolvedFilterDataInLayout(layout), '');

const layoutResultsEntityKey = state => `${RESULTS_LAYOUT}_${selectCurrentPage(state)}`;
export const selectResultsLayout = state => searchLayoutByName.select(state, layoutResultsEntityKey(state));
export const selectDataFromResultsLayout = state => searchLayoutByName.data(state, layoutResultsEntityKey(state));
export const selectLoadingFromResultsLayout = state => searchLayoutByName.loading(state, layoutResultsEntityKey(state));

export const selectLocationAutocompleteFromSearchResults = state =>
  deepExtract('location_autocomplete_entry', selectResolvedFilterDataInLayout(selectDataFromResultsLayout(state)), {});
export const selectFiltersFromSearchResults = state => selectFiltersForHashInLayout(selectDataFromResultsLayout(state));
export const selectResultTypeFromSearchResults = state =>
  deepExtract('result_type', selectFiltersFromSearchResults(state));

export const makeUrlFromLayout = layout => {
  const metadata = selectMetadataInLayout(layout);
  if (metadata.activity_descriptor && metadata.hash) {
    return `${SEARCH_PATH}/${metadata.location_descriptor}/${metadata.activity_descriptor}/${metadata.hash}`;
  }
};

export const getSearchURLFromFilter = filter => {
  const { hash, content } = filter;
  const { location_descriptor: locationDescriptor, activity_descriptor: activityDescriptor } = content ?? {};

  if (!locationDescriptor || !activityDescriptor) {
    return `${SEARCH_PATH}/${hash}`;
  }
  return `${SEARCH_PATH}/${locationDescriptor}/${activityDescriptor}/${hash}`;
};

export const getSearchURLForMsaAlias = msaAlias => {
  if (msaAlias) {
    const hash = MSA_SEARCH_HASHES[msaAlias.replace(/\./g, '')]?.split('-')[0];
    if (hash) {
      return `${SEARCH_PATH}/${hash}`;
    }
  }
  return SEARCH_PATH;
};

export const isRootSearchPath = pathname => pathname === SEARCH_PATH;

// The user's location for search is async-initialized in app.saga.js
// which calls into search.saga.js' (initializeUserLocationForSearch)
export const getUserLocationForSearch = state =>
  selectActiveLocation(state) || selectUserLocation(state) || DEFAULT_GEOLOCATION;

export const selectShouldShowMap = state => selectMediaState(state).lg || selectMapView(state);

// Mobile, map-view
export const selectIsMobileMapView = state => selectIsMapToggleable(state) && selectMapView(state);
